import { memo, useCallback, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

import { useTranslate } from "helpers";

import { Button } from "ui/components/Button/Button";
import { Badge } from "ui/components/Badge";
import { Dialog } from "ui/components/Dialog";

import { deleteListingTemplateAPI, getListingTemplatesAPI } from "services/api/settings/listings/api";
import { ListingTemplate } from "services/api/settings/listings/types";

import ListingTemplateEditCard from "../ListingTemplateEditCard/ListingTemplateEditCard";
import { ListingTemplatesRowProps } from "./types";

// Popis predložaka oglasa + modal za unos — isti obrazac kao predlošci ugovora
// (ContractSaleTemplateCard): popis lijevo, gumb za dodavanje desno.
const ListingTemplatesRow: React.FC<ListingTemplatesRowProps> = () => {
  const { t } = useTranslate();

  const [templates, setTemplates] = useState<ListingTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>("");
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const fetchTemplates = useCallback(async () => {
    try {
      const results = await getListingTemplatesAPI();
      setTemplates(results);
    } catch {
      setTemplates([]);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleAddClick = () => {
    setTemplateId("");
    setShowEditModal(true);
  };

  const handleEditClick = (id: string) => {
    setTemplateId(id);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setTemplateId("");
    setShowEditModal(false);
  };

  const handleEditSuccess = () => {
    fetchTemplates();
    handleEditClose();
  };

  const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement>, id: string) => {
    event.stopPropagation();
    event.preventDefault();

    setTemplateId(id);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = async () => {
    if (!templateId) return;

    try {
      const deleted = await deleteListingTemplateAPI(templateId, t);
      if (deleted) await fetchTemplates();
    } catch {
      // greške rukuje createApiCall (snackbar)
    }

    setTemplateId("");
    setOpenDialog(false);
  };

  const handleDeleteDialogClose = () => {
    setTemplateId("");
    setOpenDialog(false);
  };

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px]">
          <p className="text-base font-bold text-slate-600 py-3">Predlošci oglasa</p>
          <p className="text-sm text-slate-500">
            Unaprijed pripremljeni naslov i opis oglasa s varijablama koje se popune podacima vozila.
          </p>
        </div>

        <div className="flex flex-1 px-3 my-3">
          <div className="flex flex-1 flex-col">
            <div className="prose max-w-2xl text-sm border border-slate-200 p-4 rounded-sm bg-white mb-3">
              <p className="mb-1">Predlošci ti omogućuju da:</p>
              <ul className="list-disc pl-5 mb-3">
                <li>jednom napišeš naslov i opis koji vrijede za sva vozila</li>
                <li>u tekst ubaciš varijable poput {"{marka}"}, {"{model}"} ili {"{kilometraza}"}</li>
                <li>novi oglas otvoriš s već popunjenim sadržajem</li>
              </ul>
              <p className="mb-0">
                Predložak označen kao <strong>zadani</strong> primjenjuje se automatski na novi oglas, a
                ostale možeš odabrati u koraku „Podaci oglasa".
              </p>
            </div>

            <div className="flex justify-between gap-3">
              <div>
                {templates.length > 0 && (
                  <div className="rounded-sm bg-white border mb-3">
                    <div className="min-h-[56px] min-w-[300px] max-w-2xl divide-y divide-slate-200">
                      {templates.map((template) => (
                        <div
                          key={template.id}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 min-h-[56px]"
                          onClick={() => handleEditClick(template.id)}
                        >
                          <div className="flex justify-between items-center w-full gap-3">
                            <div className="text-sm">{template.name}</div>
                            {template.isDefault && (
                              <Badge text="Zadani" variant="warning" height="small" showDot />
                            )}
                          </div>
                          <span
                            onClick={(e) => handleDeleteClick(e, template.id)}
                            className="w-10 h-10 flex items-center justify-center cursor-pointer bg-red-50 hover:bg-red-100 rounded-full shrink-0"
                          >
                            <ReactSVG src="/media/icons/trash.svg" className="w-4 h-4 stroke-[#bd0000]" />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Button
                  variant="outlined"
                  children="Dodaj predložak"
                  onClick={handleAddClick}
                  startIcon="/media/icons/plus.svg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ListingTemplateEditCard
        show={showEditModal}
        id={templateId}
        onSuccess={handleEditSuccess}
        onClose={handleEditClose}
      />

      <Dialog
        open={openDialog}
        title="Brisanje predloška"
        message="Jeste li sigurni da želite obrisati ovaj predložak oglasa?"
        confirmText={t("admin.delete")}
        cancelText={t("admin.cancel")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

export default memo(ListingTemplatesRow);
